<template>
    <div
        :class="[$style['mds-notification'], classObject, variationClass, sizeClass]"
        @mouseover="stopDismissDelay"
        @mouseleave="startDismissDelay"
        :data-mds-version="version"
    >
        <div :class="$style['mds-notification__variation']">
            <mds-icon :name="variationIcon" :class="$style['mds-notification__icon']" :size="iconSize" />
        </div>
        <div :class="$style['mds-notification__body']">
            <div :class="$style['mds-notification__content']">
                <div v-if="title" :class="$style['mds-notification__title']">{{ title }}</div>
                <p :class="$style['mds-notification__message']">
                    <slot>{{ text }}</slot>
                </p>
                <ul v-if="list" :class="$style['mds-notification__list']">
                    <li v-for="(item, index) in list" :key="index" :class="$style['mds-notification__list-item']">
                        {{ item }}
                    </li>
                </ul>
                <slot name="mds-notification-custom-action"></slot>
            </div>
        </div>
        <mds-button
            v-if="!persistent"
            :class="$style['mds-notification__dismiss-button']"
            type="button"
            :size="dismissButtonSize"
            icon="remove"
            @click="$emit('mds-notification-dismissed', $event.target, $event)"
            variation="icon-only"
            :text="dismissIconAriaLabel"
        >
        </mds-button>
    </div>
</template>

<script>
    import MdsIcon from '@mds/icon-vue3';
    import { MdsButton } from '@mds/button-vue3';
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsNotification',
        components: {
            MdsIcon,
            MdsButton,
        },
        data() {
            return {
                timer: null,
                version: `${name}-${version}`,
            };
        },
        emits: ['mds-notification-dismissed'],
        props: {
            dismissDelay: {
                type: Number,
                default: 5000,
                docs: {
                    validation: '—',
                    description: 'Sets the delay time before the `mds-notification-dismissed` event is fired.',
                },
            },
            dismissIconAriaLabel: {
                type: String,
                default: 'Dismiss',
                docs: {
                    validation: '—',
                    description: 'Sets the aria label for the dismiss button.',
                },
            },
            list: {
                type: Array,
                docs: {
                    validation: '—',
                    description: 'Sets the notification’s unordered list content.',
                },
            },
            persistent: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, removes the dismiss button.',
                },
            },
            role: {
                type: String,
                default: 'alert',
                validator(value) {
                    return ['alert', 'alertdialog'].indexOf(value) !== -1;
                },
                docs: {
                    validation: '—',
                    description: 'Sets the notification role.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`.',
                    description: 'Sets the size of the notification.',
                },
            },
            text: {
                type: String,
                docs: {
                    validation: '**Required**',
                    description: 'Sets the notification text.',
                },
            },
            tinted: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, adds the tinted background color to the body of the notification.',
                },
            },
            title: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the notification title.',
                },
            },
            variation: {
                type: String,
                default: 'informational',
                validator(value) {
                    return ['error', 'informational', 'success', 'warning'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `error`, `warning`, `success`, `informational`.',
                    description: 'Sets the variation of the alert.',
                },
            },
        },
        computed: {
            classObject() {
                const obj = {};
                obj[this.$style['mds-notification--tinted']] = this.tinted;
                obj[this.$style['mds-notification--dismissible']] = !this.persistent;
                return obj;
            },
            iconSize() {
                return this.size === 'small' ? 'small' : 'medium';
            },
            sizeClass() {
                return this.$style[`mds-notification--${this.size}`];
            },
            variationClass() {
                return this.$style[`mds-notification--${this.variation}`];
            },
            variationIcon() {
                const icon = {
                    error: 'status-error',
                    warning: 'status-warning',
                    success: 'status-success',
                    informational: 'status-informational',
                };

                return icon[this.variation];
            },
            dismissButtonSize() {
                return this.size === 'large' ? 'medium' : 'small';
            },
            hasCustomAction() {
                return !!this.$slots['mds-notification-custom-action'];
            },
        },
        mounted() {
            if (!this.hasCustomAction) {
                this.startDismissDelay();
            }
        },
        methods: {
            startDismissDelay() {
                if (!this.persistent) {
                    this.timer = setTimeout(() => {
                        this.$emit('mds-notification-dismissed');
                    }, this.dismissDelay);
                }
            },
            stopDismissDelay() {
                clearTimeout(this.timer);
                this.timer = null;
            },
        },
    };
</script>

<style lang="scss" module>
    @use './notification.scss' as *;
</style>
